import { useState, useCallback, useId } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowRight, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Card } from '@/components/ui/Card'
import { startupService, ApiError } from '@/services'
import { useAppStore } from '@/stores/appStore'
import type { StartupCreateRequest, StartupStage } from '@/types/api'

const NAME_MAX = 120
const DESCRIPTION_MAX = 300

const STAGE_OPTIONS: Array<{ value: StartupStage; label: string }> = [
  { value: 'idea', label: 'Idea' },
  { value: 'mvp', label: 'MVP' },
  { value: 'pre_seed', label: 'Pre-seed' },
  { value: 'seed', label: 'Seed' },
  { value: 'series_a', label: 'Series A' },
  { value: 'series_b_plus', label: 'Series B+' },
]

/**
 * First-run setup. Single card, three fields, one button.
 *
 * The name is required — every subsequent API call needs a `startupId` and
 * the only way to make one is to POST /startups. The one-liner and stage
 * are optional but cheap to ask for, and they make the empty Overview feel
 * less empty (the title and description show up on day one).
 *
 * On success we seed appStore from the response (startup + membership in a
 * single payload, courtesy of PR #66) so the next render has a startupId
 * before AppShell mounts useStartupBootstrap. This avoids a "no startup
 * yet → redirect to /onboarding → already there → infinite loop" flash.
 */
export function OnboardingPage() {
  const navigate = useNavigate()
  const setActiveStartup = useAppStore((s) => s.setActiveStartup)

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [stage, setStage] = useState<StartupStage>('pre_seed')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const nameId = useId()
  const descId = useId()
  const stageId = useId()

  const trimmedName = name.trim()
  const canSubmit =
    !submitting && trimmedName.length > 0 && trimmedName.length <= NAME_MAX

  const handleSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault()
      if (!canSubmit) return

      setSubmitting(true)
      setError(null)

      const payload: StartupCreateRequest = {
        name: trimmedName,
        stage,
      }
      const trimmedDesc = description.trim()
      if (trimmedDesc) payload.description = trimmedDesc

      try {
        const created = await startupService.create(payload)
        setActiveStartup(created)
        navigate('/overview', { replace: true })
      } catch (err) {
        const message = friendlyError(err)
        setError(message)
        setSubmitting(false)
      }
    },
    [canSubmit, trimmedName, stage, description, setActiveStartup, navigate]
  )

  return (
    <div className="min-h-screen bg-bg-deepest flex items-center justify-center p-6">
      <Card className="w-full max-w-md p-8 bg-bg-primary border-border-default">
        <header className="mb-6">
          <h1 className="text-2xl font-display font-semibold text-text-primary tracking-tight">
            Let’s set up your context engine.
          </h1>
          <p className="mt-2 text-sm text-text-secondary">
            Tell us what you’re building. We’ll start tracking everything from
            here.
          </p>
        </header>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
          <div>
            <label
              htmlFor={nameId}
              className="block text-sm font-medium text-text-primary mb-1.5"
            >
              Company or project name
            </label>
            <Input
              id={nameId}
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={NAME_MAX}
              placeholder="ContextMD"
              autoComplete="organization"
              autoFocus
              required
              aria-required="true"
              aria-describedby={`${nameId}-help`}
            />
            <p
              id={`${nameId}-help`}
              className="mt-1 text-xs text-text-tertiary"
            >
              You can change this later in Settings.
            </p>
          </div>

          <div>
            <label
              htmlFor={descId}
              className="block text-sm font-medium text-text-primary mb-1.5"
            >
              In one line, what does it do?{' '}
              <span className="text-text-tertiary font-regular">
                (optional)
              </span>
            </label>
            <textarea
              id={descId}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={DESCRIPTION_MAX}
              rows={3}
              placeholder="Your context engine, built from day one."
              className="w-full px-3 py-2 rounded-md border border-border-default bg-bg-primary text-sm text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors resize-y"
            />
          </div>

          <div>
            <label
              htmlFor={stageId}
              className="block text-sm font-medium text-text-primary mb-1.5"
            >
              Stage{' '}
              <span className="text-text-tertiary font-regular">(optional)</span>
            </label>
            <select
              id={stageId}
              value={stage}
              onChange={(e) => setStage(e.target.value as StartupStage)}
              className="w-full h-10 px-3 rounded-md border border-border-default bg-bg-primary text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors"
            >
              {STAGE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {error && (
            <div
              role="alert"
              className="text-sm px-3 py-2 rounded-md border border-error/30 bg-error/10 text-text-primary"
            >
              {error}
            </div>
          )}

          <Button
            type="submit"
            disabled={!canSubmit}
            className="mt-2 w-full h-11 gap-2"
          >
            {submitting ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Creating workspace…
              </>
            ) : (
              <>
                Continue
                <ArrowRight size={16} />
              </>
            )}
          </Button>
        </form>
      </Card>
    </div>
  )
}

function friendlyError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 400) {
      return err.message || 'Please double-check the name and try again.'
    }
    if (err.status === 401) {
      return 'Your session expired. Please sign in again.'
    }
    if (err.status >= 500) {
      return 'Something went wrong on our end. Try again in a moment.'
    }
    return err.message
  }
  if (err instanceof Error) return err.message
  return 'Something unexpected happened. Try again in a moment.'
}
